import { $tutorial } from '@/stores/dialog';
import { useStore } from '@nanostores/react';
import { Joyride, type Step } from 'react-joyride';

const steps: Step[] = [
  {
    target: '#new-log',
    content: 'This is where you can add a new log entry.',
  },
  {
    target: '#new-log .create-log-callsign',
    content: (
      <>
        This input will automatically be focused when you add a new log entry.
        Enter the callsign of the station you are communicating with. The
        formats accepted are{' '}
        <ul className="list-inside list-disc text-left">
          <li>
            <code>FROM &gt; TO</code>
          </li>
          <li>
            <code>FROM -&gt; TO</code>
          </li>
          <li>
            <code>FROM - TO</code>
          </li>
          <li>
            <code>&gt; TO</code> inferring <code>FROM</code> as your station
          </li>
          <li>
            <code>FROM &gt;</code> inferring <code>TO</code> as your station
          </li>
          <li>
            <code>FROM</code> inferring <code>TO</code> as your station
          </li>
        </ul>
        .
      </>
    ),
  },
  {
    target: '#new-log .create-log-time',
    content:
      'Enter the time of the communication. If left blank, it will default to the current time.',
  },
  {
    target: '#new-log .create-log-description',
    content: (
      <>
        Describe the communication in this field. Typing a report, such as{' '}
        <code>SITREP</code>, and pressing <code>Enter</code> will expand it into
        a full report template. Typing an MGRS coordinate and pressing{' '}
        <code>Space</code> will automatically format it. Code words, such as{' '}
        <code>NODUFF</code>, will be underlined and can be hovered over for more
        information. You can customize reports and code words in{' '}
        <code>Edit</code> &gt; <code>Edit Reports</code> or{' '}
        <code>Edit Codewords</code>.
      </>
    ),
  },
  {
    target: '#new-log-save',
    content: (
      <>
        Click this button to save the new log entry. You can also press{' '}
        <code>Ctrl + Enter</code> (or <code>Cmd + Enter</code> on Mac).
      </>
    ),
  },
  {
    target: '#new-log-save-dropdown',
    content:
      'Use this dropdown to add a draft log. Drafts are highlighted in yellow, time is optional, and you can undraft by hovering the log number and clicking it (or tab to it).',
  },
  {
    target: '#new-log .create-log-tags',
    content:
      'Add tags to your log entry for better organization. Tags can be managed in Edit > Manage Tags.',
  },
  {
    target: '#new-log .create-log-frequency',
    content:
      'Enter the frequency of the communication here if it is different from the default frequency.',
  },
  {
    target: '#logbook-header',
    content: 'This is where you can assign the metadata for the logbook.',
  },
  {
    target: '#header-assignment',
    content: 'Enter the assignment name for the mission here.',
  },
  {
    target: '#header-date',
    content:
      "Select the date for the logbook entries here. Defaults to today's date.",
  },
  {
    target: '#header-callsign',
    content:
      "Enter your station's callsign here. This will be used to infer callsigns in log entries.",
  },
  {
    target: '#header-frequency',
    content:
      'Set the default frequency for communications in this logbook here.',
  },
  {
    target: '#operator-header',
    content:
      "This section displays the current operator's information for the log entries.",
  },
  {
    target: '#change-operator-button',
    content:
      'Click this button to change the current operator. This will update the operator information for new log entries. In the operator selection dialog, you can choose from existing operators or add a new one.',
  },
  {
    target: 'body',
    placement: 'center',
    content:
      "Entries can be edited after the fact. All modifications are logged and can be viewed in the Audit Log. To remove an entry, you can click hover over the number next to it and click the 'X' that appears.",
  },
  {
    target: 'body',
    placement: 'center',
    content:
      'To open the Officer View, which is a read-only view of the logs suitable for having open on a big screen during operations, go to View > New Officer Window. Changes are reflected in real-time.',
  },
];

export function Tutorial() {
  const isOpen = useStore($tutorial);

  return (
    <Joyride
      steps={steps}
      continuous
      run={isOpen}
      onEvent={data => {
        if (data.status === 'finished' || data.status === 'skipped') {
          $tutorial.set(false);
        }
      }}
      options={{
        scrollOffset: 80,
        zIndex: 10000,
        buttons: ['back', 'close', 'primary', 'skip'],
      }}
    />
  );
}
